import React, { createContext, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';

// Define the theme colors
const theme = {
  primary: 'rgba(64,90,103, 1)',
  'primary-light': 'rgb(81, 112, 129)',
  'primary-faded': 'rgba(64,90,103, 0.05)',
  secondary: 'rgb(166,177,179)',
  'secondary-light': 'rgba(214, 226, 228, 1)',
  success: 'rgba(119, 189, 30)',
  danger: '#dc3545',
  error: '#ff4d4f',
  info: '#17a2b8',
  warning: '#e59864',
  orange: '#FF892F',
  white: '#ffffff',
  black: '#000000',
  headerHeight: "60px",
  bottomViewHeight: "15rem",
  grey: '#6c757d',
};

// Create a Theme Context
const ThemeContext = createContext<any>(null);

// Utility function to generate dynamic classes for theme colors
export const generateThemeClasses = () => {
  const classes: { [key: string]: string } = {};

  Object.keys(theme).forEach((key) => {
    classes[`bg-${key}`] = theme[key as keyof typeof theme];
    classes[`color-${key}`] = theme[key as keyof typeof theme];
    // Fix: For faded, use hex with appended 33 if possible, else fallback to rgba with alpha
    const color = theme[key as keyof typeof theme];
    let fadedColor = color;
    if (color.startsWith('#')) {
      // If hex, append 33 for ~20% opacity
      fadedColor = color + '33';
    } else if (color.startsWith('rgb(')) {
      // Convert rgb to rgba with alpha 0.2
      fadedColor = color.replace('rgb(', 'rgba(').replace(')', ', 0.2)');
    } else if (color.startsWith('rgba(')) {
      // Already rgba, just replace alpha
      fadedColor = color.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/, 'rgba($1,$2,$3,0.2)');
    }
    classes[`faded-${key}`] = fadedColor;
    classes[`border-${key}`] = theme[key as keyof typeof theme];
    classes[`hover-${key}`] = fadedColor;

  });

  return classes;
};

// Function to inject dynamic styles into the document
const injectDynamicStyles = (classes: { [key: string]: string }) => {
  console.log('Injecting dynamic styles:', classes);
  const styleElement = document.createElement('style');
  let styles = '';

  Object.entries(classes).forEach(([className, color]) => {
    if (className.startsWith('bg-')) {
      styles += `.${className} { background-color: ${color}; }\n`;
    } else if (className.startsWith('color-')) {
      styles += `.${className} { color: ${color}; }\n`;
    } else if (className.startsWith('faded-')) {
      styles += `.${className} { background-color: ${color}; }\n`;
    } else if (className.startsWith('border-')) {
      styles += `.${className} { border: 1px solid ${color}; }\n`;
    } else if (className.startsWith('hover-')) {
      styles += `.${className}:hover { background-color: ${color}; }\n`;
    }
  });

  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
};

// Theme Provider Component
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    const classes = generateThemeClasses();
    injectDynamicStyles(classes);
  }, []);

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom Hook to use the Theme
export const useTheme = () => {
  return useContext(ThemeContext);
};

